import React, { useState } from "react";
import { useValidateBetsMutation } from "@/api/betting";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info, Loader2, Globe, Database, Calendar } from "lucide-react";

interface ValidateModalProps {
  isOpen: boolean;
  onClose: () => void;
  persona: any;
}

const ValidateModal: React.FC<ValidateModalProps> = ({
  isOpen,
  onClose,
  persona,
}) => {
  const { toast } = useToast();
  const validateBets = useValidateBetsMutation();
  const [daysBack, setDaysBack] = useState(7);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleValidate = () => {
    setIsSubmitting(true);

    validateBets.mutate(
      {
        persona_id: persona.persona_id,
        days_back: daysBack,
      },
      {
        onSuccess: (response) => {
          toast({
            description: `Validation job started: ${response.job_id}`,
          });
          setIsSubmitting(false);
          onClose();
        },
        onError: (error) => {
          toast({
            variant: "destructive",
            description: `Error starting validation job: ${error.message}`,
          });
          setIsSubmitting(false);
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Globe className="h-5 w-5 text-blue-400" />
            Validate Betting Results
          </DialogTitle>
          <DialogDescription>
            Validate betting predictions for {persona?.name} using automated
            sports data.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-5">
          <div className="bg-black/30 border border-zinc-800 rounded-lg p-4">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-400" />
              Time Period
            </h3>
            <div className="space-y-2">
              <Label htmlFor="days-back" className="text-sm text-zinc-400">
                Days to Look Back
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="days-back"
                  type="number"
                  min={1}
                  max={30}
                  value={daysBack}
                  onChange={(e) => setDaysBack(parseInt(e.target.value) || 7)}
                  className="bg-black/20 border-zinc-700"
                />
                <span className="text-sm text-zinc-400">days</span>
              </div>
              <p className="text-xs text-zinc-500">
                Validate bets created within the last {daysBack} days
              </p>
            </div>
          </div>

          <div className="bg-black/30 border border-zinc-800 rounded-lg p-4">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <Database className="h-4 w-4 text-blue-400" />
              Validation Sources
            </h3>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div className="bg-blue-950/20 border border-blue-900 rounded-md p-2">
                <h4 className="text-sm font-medium text-blue-400">
                  Google Search
                </h4>
                <p className="text-xs text-zinc-400 mt-1">
                  Secure, authenticated access to web search for score
                  verification
                </p>
              </div>
              <div className="bg-purple-950/20 border border-purple-900 rounded-md p-2">
                <h4 className="text-sm font-medium text-purple-400">
                  Tavily API
                </h4>
                <p className="text-xs text-zinc-400 mt-1">
                  Dedicated sports data lookup for cross-referencing
                </p>
              </div>
            </div>
            <p className="text-xs text-zinc-500">
              The system will verify bets using multiple trusted sports data
              sources
            </p>
          </div>

          <Alert className="bg-blue-950/20 border-blue-800">
            <Info className="h-4 w-4" />
            <AlertTitle className="text-blue-400">About Validation</AlertTitle>
            <AlertDescription className="text-xs text-zinc-300">
              The system will use sports data APIs and Google Search to validate
              the outcome of betting predictions. This process will verify:
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Exact date, time, and venue of events</li>
                <li>Complete team/participant information</li>
                <li>Final scores and outcomes</li>
                <li>Verification against the original bet prediction</li>
              </ul>
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleValidate}
            disabled={isSubmitting}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              "Start Validation"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ValidateModal;
